import React, { useState, useEffect } from "react";
import { API_BASE, authHeaders, useAgency } from "./AgencyContext";

const G = "#0077B5";

export default function AgencyInviteForm() {
  const { refresh } = useAgency();
  const [mode, setMode] = useState("single"); // single | bulk
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("presence");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  // Pending invites
  const [pending, setPending] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/app/api/agency/clients?status=invited`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { clients: [] })
      .then(d => setPending(d.clients || []))
      .catch(() => {});
  }, [result, bulkResult]);

  const sendSingle = async () => {
    if (!name.trim() || !email.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/clients`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ client_name: name.trim(), client_email: email.trim(), plan }),
      });
      const d = await r.json();
      if (r.ok) {
        setResult({ ok: true, link: d.invite_link });
        setName(""); setEmail("");
        refresh();
      } else {
        setResult({ ok: false, error: d.error || "Failed" });
      }
    } catch {
      setResult({ ok: false, error: "Network error" });
    }
    setSending(false);
  };

  const sendBulk = async () => {
    const lines = bulkText.trim().split("\n").filter(l => l.includes(","));
    if (lines.length === 0) return;
    const clients = lines.map(l => {
      const parts = l.split(",").map(s => s.trim());
      return { name: parts[0], email: parts[1], plan: parts[2] || plan };
    }).filter(c => c.name && c.email);
    if (clients.length === 0) return;
    setSending(true);
    setBulkResult(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/clients/bulk`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ clients }),
      });
      const d = await r.json();
      setBulkResult(d);
      refresh();
    } catch {
      setBulkResult({ error: "Network error" });
    }
    setSending(false);
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 20 }}>Invite clients</div>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[["single", "Single"], ["bulk", "Bulk (CSV)"]].map(([id, label]) => (
          <button key={id} onClick={() => setMode(id)} style={{
            background: mode === id ? G : "#fff", color: mode === id ? "#fff" : "#666",
            border: "1px solid " + (mode === id ? G : "rgba(0,0,0,0.1)"),
            borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer",
          }}>{label}</button>
        ))}
      </div>

      {mode === "single" ? (
        <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Client name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" type="email" style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", fontSize: 13, boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Plan</label>
            <select value={plan} onChange={e => setPlan(e.target.value)} style={{ padding: "10px 12px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", fontSize: 13 }}>
              <option value="presence">Presence</option>
              <option value="authority">Authority</option>
            </select>
          </div>
          <button onClick={sendSingle} disabled={sending || !name.trim() || !email.trim()} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: sending ? 0.6 : 1 }}>
            {sending ? "Sending…" : "Send Invite"}
          </button>
          {result && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 6, background: result.ok ? "#E1F5EE" : "#FAE4E4", color: result.ok ? "#0F6E56" : "#8B1A1A", fontSize: 13 }}>
              {result.ok ? <>Invite sent! Link: <code style={{ fontSize: 11 }}>{result.link}</code></> : result.error}
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Paste CSV (name, email per line — max 50)</label>
          <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={6} placeholder={"Jane Smith, jane@company.com\nJohn Doe, john@company.com"} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", fontSize: 13, fontFamily: "monospace", boxSizing: "border-box", resize: "vertical" }} />
          <button onClick={sendBulk} disabled={sending || !bulkText.trim()} style={{ marginTop: 12, background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: sending ? 0.6 : 1 }}>
            {sending ? "Sending…" : "Send All Invites"}
          </button>
          {bulkResult && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 6, background: "#E1F5EE", color: "#0F6E56", fontSize: 13 }}>
              Added: {bulkResult.added}, Skipped: {bulkResult.skipped}, Failed: {bulkResult.failed}
            </div>
          )}
        </div>
      )}

      {/* Pending invites */}
      {pending.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Pending invites ({pending.length})</div>
          {pending.map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid rgba(0,0,0,0.05)", fontSize: 13 }}>
              <div>
                <span style={{ fontWeight: 500 }}>{c.client_name}</span>
                <span style={{ color: "#888", marginLeft: 8 }}>{c.client_email}</span>
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                Expires {c.invite_expires_at ? new Date(c.invite_expires_at).toLocaleDateString() : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
